import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getProfilePhotoUrl, getCoverUrl } from "@/lib/mediaUrls";
import SinglesEarningsPanel from "@/components/SinglesEarningsPanel";

/**
 * Phase 7.1 Milestone B — Artist Earnings Dashboard
 * ==================================================
 *
 * Post Payout Funding Safety remediation (Feb 2026): every "$" on this
 * page must be sourced from a verified, paid-backed field — either the
 * cashable balance or the pending-clearance balance served by
 * ``/api/payouts/me/summary`` (which internally uses the $0.0075
 * payout rate and the paid-source ``earnings_entries`` ledger). The
 * old ``estimated_usd`` field (from ``/api/earnings/me``, derived from
 * ``total_earned_coins × $0.01``) is intentionally NOT shown next to a
 * "$" anywhere — it can include reward/test coins that are never
 * cashable. Coins remain first-class throughout, but reward-inflated
 * dollar numbers are gone.
 */

const formatCoins = (n) => Number(n || 0).toLocaleString();
const formatUsd = (n) => `$${Number(n || 0).toFixed(2)}`;

const StatCard = ({ label, value, sub, accent = "purple", testId, icon }) => {
  const accentMap = {
    purple: "from-purple-500/15 to-fuchsia-500/10 border-purple-500/30",
    pink: "from-pink-500/15 to-rose-500/10 border-pink-500/30",
    amber: "from-amber-400/20 to-yellow-500/10 border-amber-500/40",
    emerald: "from-emerald-400/15 to-teal-500/10 border-emerald-500/30",
    blue: "from-blue-500/15 to-cyan-500/10 border-blue-500/30",
    rose: "from-rose-500/15 to-pink-500/10 border-rose-500/30",
  };
  return (
    <Card
      className={`p-4 sm:p-5 bg-gradient-to-br ${accentMap[accent] || accentMap.purple} border`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-white/70">
          {label}
        </p>
        {icon && <span className="text-xl">{icon}</span>}
      </div>
      <p className="text-2xl sm:text-3xl font-black text-white mt-2 leading-none">{value}</p>
      {sub && <p className="text-xs text-white/60 mt-1.5">{sub}</p>}
    </Card>
  );
};

const formatTimeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
};

const EarningsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [walletDetails, setWalletDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.get("/auth/me");
      } catch (_e) {
        if (!cancelled) navigate("/login", { replace: true });
        return;
      }
      try {
        // Fetch three surfaces in parallel — the earnings dashboard
        // (coin totals + history), the payout summary (verified
        // paid-backed dollar values via $0.0075), and the wallet
        // (reward vs paid vs test bucket separation).
        const [e, p, w] = await Promise.all([
          api.get("/earnings/me"),
          api.get("/payouts/me/summary"),
          api.get("/wallet/me"),
        ]);
        if (cancelled) return;
        setData(e.data);
        setPayoutSummary(p.data);
        setWalletDetails(w.data?.wallet || w.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load earnings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08060f] text-white flex items-center justify-center">
        <div className="animate-pulse text-pink-200/70 text-sm" data-testid="earnings-loading">
          Loading earnings…
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#08060f] text-white flex items-center justify-center">
        <p className="text-pink-200/70">No data yet.</p>
      </div>
    );
  }

  const {
    wallet_balance,
    totals,
    weekly,
    monthly,
    most_gifted_track,
    top_supporter,
    fan_boosted_track_count,
    history = [],
  } = data;

  // Payout Funding Safety (Feb 2026) — every "$" on this page reads
  // from /payouts/me/summary (paid-backed, $0.0075) rather than the
  // /earnings/me estimated_usd (mixes paid + reward + test, $0.01).
  const cashableUsd = Number(payoutSummary?.spendable_available_usd || 0);
  const cashableCoins = Number(payoutSummary?.spendable_available_coins || 0);
  const pendingUsd = Number(payoutSummary?.pending_usd || 0);
  const pendingCoins = Number(payoutSummary?.pending_coins || 0);
  const minPayoutUsd = Number(payoutSummary?.min_payout_usd || 25);
  const holdDays = Number(payoutSummary?.pending_hold_days || 7);
  const lastPayout = payoutSummary?.last_payout || null;

  // Reward Coins (non-withdrawable) — surfaced explicitly so artists
  // don't confuse them with cashable balance. Combines the wallet
  // reward+test buckets and the non_withdrawable_earnings counter.
  const rewardBalance = Number(walletDetails?.reward_balance || 0);
  const testBalance = Number(walletDetails?.test_balance || 0);
  const nonWithdrawableEarnings = Number(walletDetails?.non_withdrawable_earnings || 0);
  const rewardTotal = rewardBalance + testBalance + nonWithdrawableEarnings;

  const hasAnyEarnings = (totals?.received_coins || 0) > 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0514] via-[#0a0a14] to-black text-white">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-10" data-testid="earnings-page">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black tracking-tight">
              Earnings <span className="text-pink-300">💰</span>
            </h1>
            <p className="text-pink-200/70 text-xs sm:text-sm mt-1">
              Coins measure your gift activity. Only the <strong className="text-emerald-200">Cashable</strong> line
              reflects real cash-out value (verified paid-backed earnings at
              <span className="text-pink-100/90 font-semibold"> ${(payoutSummary?.payout_usd_per_earned_coin || 0.0075).toFixed(4)}/coin</span>).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/wallet"
              className="text-xs px-3 py-1.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 text-amber-200 font-semibold"
              data-testid="earnings-wallet-link"
            >
              🪙 Wallet · {formatCoins(wallet_balance)}
            </Link>
            <Link
              to="/dashboard"
              className="text-xs text-pink-300 hover:text-pink-200 underline"
              data-testid="earnings-back-dashboard"
            >
              ← Dashboard
            </Link>
          </div>
        </div>

        {/* Top stat row — post Payout Funding Safety: "Cashable Now"
            replaces the old "Estimated USD" card, which was derived
            from total_earned_coins × $0.01 and could include
            reward/test coins. */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <StatCard
            label="Cashable Now"
            value={formatUsd(cashableUsd)}
            sub={`${formatCoins(cashableCoins)} coins · verified paid-backed`}
            accent="emerald"
            icon="💵"
            testId="earnings-cashable-now"
          />
          <StatCard
            label="Pending Clearance"
            value={formatUsd(pendingUsd)}
            sub={`${formatCoins(pendingCoins)} coins · ${holdDays}-day hold`}
            accent="amber"
            icon="⏳"
            testId="earnings-pending-clearance"
          />
          <StatCard
            label="Coins Received"
            value={formatCoins(totals.received_coins)}
            sub={`${totals.gifts_received_count} gifts · not a cash balance`}
            accent="pink"
            icon="🎁"
            testId="earnings-total-received"
          />
          <StatCard
            label="Reward Coins"
            value={formatCoins(rewardTotal)}
            sub={
              <span
                className="inline-flex items-center gap-1"
                data-testid="earnings-reward-coins-tooltip"
                title="Reward Coins are earned from RGM activities such as contests, referrals and platform rewards. They can be used inside RGM Studio but cannot be withdrawn as cash."
              >
                Not withdrawable · usable inside RGM Studio
                <span aria-hidden="true" className="text-purple-200/60 cursor-help">ⓘ</span>
              </span>
            }
            accent="purple"
            icon="🎖️"
            testId="earnings-reward-coins"
          />
        </div>

        {/* Activity strip — coins only, no dollar values. */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <StatCard
            label="This Week"
            value={`${formatCoins(weekly.earned_coins)} coins`}
            sub={`${weekly.gifts_count} gifts received`}
            accent="pink"
            icon="📈"
            testId="earnings-weekly"
          />
          <StatCard
            label="This Month"
            value={`${formatCoins(monthly.earned_coins)} coins`}
            sub={`${monthly.gifts_count} gifts received`}
            accent="purple"
            icon="📅"
            testId="earnings-monthly"
          />
        </div>

        {/* Highlights row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          {/* Most Gifted Track */}
          <Card
            className="p-4 sm:p-5 bg-gradient-to-br from-rose-500/10 to-pink-500/10 border border-rose-500/30"
            data-testid="earnings-most-gifted-track"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70 flex items-center gap-1">
              🔥 Most Gifted Track
            </p>
            {most_gifted_track ? (
              <Link
                to={`/track/${most_gifted_track.submission_id}`}
                className="mt-3 flex items-center gap-3 group"
              >
                <img
                  src={getCoverUrl(most_gifted_track)}
                  alt={most_gifted_track.track_title}
                  className="w-14 h-14 rounded-lg object-cover flex-shrink-0"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=120&h=120&fit=crop"; }}
                />
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate group-hover:text-pink-200">
                    {most_gifted_track.track_title || "Untitled"}
                  </p>
                  <p className="text-xs text-pink-200/70 mt-0.5">
                    {formatCoins(most_gifted_track.coins)} coins · {most_gifted_track.gift_count} gifts
                  </p>
                </div>
              </Link>
            ) : (
              <p className="text-pink-200/60 text-sm mt-3">
                No track gifts yet. Share your music — gifts boost ranking on Trending.
              </p>
            )}
          </Card>

          {/* Top Supporter */}
          <Card
            className="p-4 sm:p-5 bg-gradient-to-br from-amber-400/15 to-yellow-500/10 border border-amber-500/40"
            data-testid="earnings-top-supporter"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70 flex items-center gap-1">
              🐐 Top Supporter
            </p>
            {top_supporter ? (
              <Link
                to={`/artist/${top_supporter.user_id}`}
                className="mt-3 flex items-center gap-3 group"
              >
                <img
                  src={getProfilePhotoUrl(top_supporter)}
                  alt={top_supporter.artist_name || top_supporter.name}
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-amber-400/60"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=120&h=120&fit=crop"; }}
                />
                <div className="min-w-0">
                  <p className="text-white font-semibold truncate group-hover:text-amber-200">
                    {top_supporter.artist_name || top_supporter.name || "Anonymous"}
                  </p>
                  <p className="text-xs text-amber-200/80 mt-0.5">
                    {formatCoins(top_supporter.coins_gifted)} coins · {top_supporter.gift_count} gifts
                  </p>
                </div>
              </Link>
            ) : (
              <p className="text-amber-200/60 text-sm mt-3">
                No supporters yet. Your first 🐐 will be honored here.
              </p>
            )}
          </Card>

          {/* Available to Cash Out — real paid-backed dollars only. */}
          <Card
            className="p-4 sm:p-5 bg-gradient-to-br from-emerald-400/10 to-teal-500/10 border border-emerald-500/30"
            data-testid="earnings-available-cashout"
          >
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70 flex items-center gap-1">
              💸 Available to Cash Out
            </p>
            <p className="text-3xl font-black text-emerald-200 mt-3">
              {formatUsd(cashableUsd)}
            </p>
            <p className="text-xs text-emerald-100/80 mt-1">
              {formatCoins(cashableCoins)} coins verified · rate ${(payoutSummary?.payout_usd_per_earned_coin || 0.0075).toFixed(4)}/coin
            </p>
            {cashableUsd < minPayoutUsd ? (
              <p className="text-[11px] text-emerald-100/70 mt-2 leading-relaxed" data-testid="earnings-progress-to-min">
                Earn {formatUsd(minPayoutUsd - cashableUsd)} more to reach the {formatUsd(minPayoutUsd)} payout minimum.
              </p>
            ) : (
              <p className="text-[11px] text-emerald-100/90 mt-2 leading-relaxed font-semibold" data-testid="earnings-eligible-to-cashout">
                ✓ Eligible to cash out. Visit the Payouts page.
              </p>
            )}
            <Link
              to="/payouts"
              state={cashableUsd >= minPayoutUsd
                ? { prefillFromCashout: true, prefillAmountUsd: cashableUsd, prefillAmountCoins: cashableCoins }
                : undefined}
              className={`inline-block mt-3 text-xs px-3 py-1.5 rounded-full border font-semibold ${
                cashableUsd >= minPayoutUsd
                  ? "bg-emerald-500 hover:bg-emerald-400 border-emerald-300 text-black"
                  : "bg-emerald-500/20 hover:bg-emerald-500/30 border-emerald-400/40 text-emerald-100"
              }`}
              data-testid={cashableUsd >= minPayoutUsd ? "earnings-cashout-cta-eligible" : "earnings-cashout-link"}
            >
              {cashableUsd >= minPayoutUsd
                ? `💰 Cash Out ${formatUsd(cashableUsd)} →`
                : "Manage Payouts →"}
            </Link>
          </Card>
        </div>

        {/* Lifetime Paid Out row (dedicated — only counts confirmed
            payouts, never inflated by unrealised coin totals). */}
        <Card
          className="p-4 sm:p-5 mb-4 sm:mb-6 bg-gradient-to-br from-slate-500/10 to-slate-700/10 border border-slate-500/30"
          data-testid="earnings-lifetime-paid-out"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70 flex items-center gap-1">
                🏦 Lifetime Paid Out
              </p>
              <p className="text-2xl font-black text-slate-100 mt-2">
                {lastPayout
                  ? formatUsd(lastPayout.amount_usd)
                  : "$0.00"}
              </p>
              <p className="text-[11px] text-slate-200/70 mt-1">
                {lastPayout
                  ? `Last: ${lastPayout.method} · ${(lastPayout.paid_at || "").slice(0, 10)}`
                  : "No completed payouts yet."}
              </p>
            </div>
            <Link
              to="/payouts"
              className="text-xs text-slate-100 hover:text-white underline decoration-dotted"
              data-testid="earnings-full-history-link"
            >
              Full history →
            </Link>
          </div>
        </Card>

        {/* Profile chips info */}
        <Card
          className="p-4 sm:p-5 mb-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30"
          data-testid="earnings-profile-chips-summary"
        >
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
            On Your Profile
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-100 text-sm font-semibold" data-testid="profile-chip-gifts">
              🎁 {formatCoins(totals.gifts_received_count)} Gifts Received
            </span>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-500/40 text-orange-100 text-sm font-semibold" data-testid="profile-chip-fan-boosted">
              🔥 {fan_boosted_track_count} Fan Boosted Track{fan_boosted_track_count === 1 ? "" : "s"}
            </span>
            {data.is_goat_supporter && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-400/20 border border-yellow-400/60 text-yellow-100 text-sm font-bold" data-testid="profile-chip-goat">
                🐐 GOAT Supporter
              </span>
            )}
          </div>
        </Card>

        {/* iter_226 · Phase 22 — Singles Store net earnings (hidden
            for regular artists while singles.enabled=false; visible
            to admins/founders in dark mode). */}
        <SinglesEarningsPanel />

        {/* History */}
        <h2 className="text-lg font-bold mb-3">Earnings History</h2>
        {history.length === 0 ? (
          <Card
            className="p-6 text-center bg-white/5 border border-white/10 text-pink-200/70 text-sm"
            data-testid="earnings-history-empty"
          >
            {hasAnyEarnings
              ? "No detailed history available yet."
              : "Once fans start sending gifts, their support will show up here."}
          </Card>
        ) : (
          <div
            className="rounded-lg overflow-hidden border border-white/10 hidden sm:block"
            data-testid="earnings-history-table"
          >
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-pink-200/80 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">Gift</th>
                  <th className="px-3 py-2 font-semibold">From</th>
                  <th className="px-3 py-2 font-semibold">On</th>
                  <th className="px-3 py-2 font-semibold text-right">Coins Earned</th>
                  <th className="px-3 py-2 font-semibold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {history.map((row) => (
                  <tr
                    key={row.transaction_id}
                    className="hover:bg-white/5"
                    data-testid={`earnings-row-${row.transaction_id}`}
                  >
                    <td className="px-3 py-2">
                      <span className="font-semibold text-white">
                        {row.gift_emoji} {row.gift_name}
                      </span>
                      <span className="block text-[10px] text-white/50">
                        +{formatCoins(row.coins)} coins
                      </span>
                    </td>
                    <td className="px-3 py-2 text-pink-100">
                      <Link to={`/artist/${row.sender.user_id}`} className="hover:underline">
                        {row.sender.artist_name || "Fan"}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-purple-100 max-w-[180px] truncate">
                      {row.target_type === "track" && row.submission_id
                        ? <Link to={`/track/${row.submission_id}`} className="hover:underline">{row.track_title || "track"}</Link>
                        : row.target_type}
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-amber-200">
                      +{formatCoins(row.earned_coins)}
                    </td>
                    <td className="px-3 py-2 text-gray-400">{formatTimeAgo(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile-friendly card list */}
        {history.length > 0 && (
          <div className="sm:hidden space-y-2 mt-2" data-testid="earnings-history-mobile">
            {history.map((row) => (
              <Card
                key={row.transaction_id}
                className="p-3 bg-white/5 border border-white/10"
                data-testid={`earnings-row-mobile-${row.transaction_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-white text-sm">
                    {row.gift_emoji} {row.gift_name}
                  </div>
                  <div className="text-right">
                    <div className="text-amber-200 font-bold text-sm">+{formatCoins(row.earned_coins)} coins</div>
                  </div>
                </div>
                <div className="mt-1 text-xs text-pink-100/80">
                  from <Link to={`/artist/${row.sender.user_id}`} className="hover:underline">{row.sender.artist_name || "Fan"}</Link>
                  {row.target_type === "track" && row.submission_id ? (
                    <> · on <Link to={`/track/${row.submission_id}`} className="hover:underline text-purple-200">{row.track_title || "track"}</Link></>
                  ) : (
                    <> · {row.target_type}</>
                  )}
                  <span className="text-gray-500"> · {formatTimeAgo(row.created_at)}</span>
                </div>
              </Card>
            ))}
          </div>
        )}

        <p className="text-[11px] text-pink-200/40 text-center mt-8">
          Cashable Now uses verified paid-backed earnings at ${(payoutSummary?.payout_usd_per_earned_coin || 0.0075).toFixed(4)}/coin.
          Reward coins and gift-received totals are activity metrics, not cash balances.
        </p>
      </div>
    </div>
  );
};

export default EarningsPage;
